import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import SimpleMarkdown from "@/components/SimpleMarkdown";
import { CopyIcon as Copy, DownloadIcon as Download, FileTextIcon as FileText } from "@phosphor-icons/react";

/**
 * Admin doc viewer.
 *
 * Renders a markdown PRD/spec doc from `/api/admin/docs/{key}`. Used
 * for in-app reading of long-form planning documents (e.g. the
 * Service Providers PRD) without sending the operator into a code
 * editor or markdown viewer.
 *
 * Route: `/admin/docs/:key`
 */
export default function AdminDocViewer() {
  const { key } = useParams();
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/admin/docs/${key}`)
      .then((r) => setDoc(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Could not load doc"));
  }, [key]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(doc.content);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Copy failed");
    }
  };

  const download = () => {
    const blob = new Blob([doc.content], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${key}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (err) return (
    <AppShell>
      <div className="text-crimson">{err}</div>
    </AppShell>
  );
  if (!doc) return (
    <AppShell>
      <div className="text-[#6B6B6B]">Loading doc…</div>
    </AppShell>
  );

  return (
    <AppShell>
      <div className="max-w-4xl">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-2">
          <div className="flex-1 min-w-0">
            <div className="inline-flex items-center gap-2 mb-3">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-cream ring-1 ring-gold/40">
                <FileText size={14} weight="duotone" className="text-[#8A6A00]" />
              </span>
              <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold">Internal spec</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={copy} variant="outline" size="sm" className="rounded-full" data-testid="doc-copy-btn">
              <Copy size={13} weight="duotone" className="mr-1" />
              Copy
            </Button>
            <Button onClick={download} variant="outline" size="sm" className="rounded-full" data-testid="doc-download-btn">
              <Download size={13} weight="duotone" className="mr-1" />
              Download .md
            </Button>
          </div>
        </div>

        <div className="prose-luxury" data-testid="doc-content">
          <SimpleMarkdown source={doc.content} />
        </div>
      </div>
    </AppShell>
  );
}
